/**
 * "New task" as a CRM dialog.
 *
 * Was a bordered box with shadcn fields inside `ModalShell`; it is the design's
 * own `Modal` now, with `crm-lab` labels over `crm-inp` pill fields, so it
 * matches every other dialog in the app.
 *
 * It also carries the start date, the estimate and the billable flag. The API
 * has accepted all three since the task table was written, and nothing in the
 * product set them: a task created here was billable because Prisma's default
 * said so, not because anybody decided it, and no estimate ever reached the
 * record. The place to decide that is while the task is being described - going
 * back to edit it afterwards is a step people skip.
 *
 * Two more of exactly that kind arrived with R3: WHO is on the task, and WHICH
 * PHASE of the project it belongs to. Both columns are as old as the table and
 * neither had a control anywhere in the product, which is why production's
 * imported tasks have no owner at all. A task described without an owner is a
 * task somebody has to come back and assign, and coming back is the step that
 * does not happen.
 *
 * The phase question only exists once a project has been named, so it is drawn
 * only then, and it is asked with an explicit "no milestone" option rather than
 * an empty field: not filing a task under a phase is a decision somebody takes,
 * not the absence of one. That pair - and the rule that a project change clears
 * the phase - now lives in `_shared/ProjectPhaseFields`, because the support
 * desk files tickets as tasks through a dialog of its own and two copies of that
 * rule is one copy away from a dialog that forgets it.
 *
 * The dropdowns and the tick boxes are the app's own controls, not the browser's.
 * A native `<select>` inside a `.crm-inp` pill shows no arrow and then drops a
 * grey operating system list over the dialog, and a native tick box paints the
 * OS square beside labels this app drew - so the dialog now uses `MenuField` and
 * `CheckField`, the same pair the ticket and time dialogs use. That needs a
 * `PopHost` around the form: `Modal` mounts none, and a `Pop` without one is a
 * field that can be focused and pressed and never opens. It is mounted HERE
 * rather than left to the caller because this dialog is opened from two screens
 * and one of them (the project page) has no host of its own.
 *
 * The last question is the one with an audience outside the company: whether the
 * customer sees this task in their portal. It defaults to visible, which is what
 * the column does too, but it is asked here rather than inherited silently - the
 * moment to decide that a piece of work stays private is while you are writing
 * down what it is, and a task nobody chose to hide is a task the client can read.
 */
import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { DatePicker } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { createTask, type Project, type TaskPriority } from "../../../lib/projects";
import { toIso } from "../projects/lib";
import { Btn, CheckField, MenuField, Modal } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { ProjectPhaseFields } from "../_shared/ProjectPhaseFields";
import { UserPicker } from "../_shared/UserPicker";
import { PRIORITIES } from "./priority";
import { taskErrorKey } from "./taskErrors";

export function NewTaskModal({
  projects,
  defaultProjectId,
  onClose,
  onCreated,
}: {
  projects: Project[];
  /** Pre-selects the project when the dialog is opened from a filtered list. */
  defaultProjectId?: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const [title, setTitle] = useState("");
  const [projectId, setProjectId] = useState(defaultProjectId ?? "");
  const [milestoneId, setMilestoneId] = useState("");
  const [assignees, setAssignees] = useState<string[]>([]);
  const [priority, setPriority] = useState<TaskPriority>("normal");
  const [due, setDue] = useState("");
  const [start, setStart] = useState("");
  const [estimate, setEstimate] = useState("");
  // Billable by default, which is what the column does too - but visibly, so
  // the operator can see the decision and take it back before the task exists.
  const [billable, setBillable] = useState(true);
  // Visible to the customer by default, for the same reason and with a sharper
  // edge: the default here has to match the column's, or a task created in this
  // dialog would behave differently from one the quick-add field makes.
  const [visibleToClient, setVisibleToClient] = useState(true);
  const [busy, setBusy] = useState(false);
  /** The catalogue key for the last refusal, not its text - see `taskErrors`. */
  const [errorKey, setErrorKey] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    const hours = Number(estimate.trim());
    setBusy(true);
    setErrorKey(null);
    try {
      await createTask({
        title: title.trim(),
        priority,
        projectId: projectId || undefined,
        milestoneId: milestoneId || undefined,
        // Omitted rather than sent as an empty list: "nobody was named" is the
        // column's own default, and there is nothing to say about it.
        assignees: assignees.length ? assignees : undefined,
        dueDate: toIso(due),
        startDate: toIso(start),
        // An empty field leaves the column null rather than sending a zero: an
        // estimate of nought is a claim that the task takes no work.
        estimateHours: estimate.trim() === "" || !Number.isFinite(hours) ? undefined : hours,
        billable,
        visibleToClient,
      });
      onCreated();
      onClose();
    } catch (err) {
      // The dialog stays open and says why. Before this it closed over an
      // unhandled rejection and the task simply did not exist.
      setErrorKey(taskErrorKey(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={t("tasks.addTitle")} label={t("tasks.addTitle")} onClose={onClose} className="max-w-[460px]">
      <PopHost>
        <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
          <div>
            <span className="crm-lab">{t("tasks.titleField")} *</span>
            <label className="crm-inp">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("tasks.titlePlaceholder")}
                autoComplete="off"
                autoFocus
                required
              />
            </label>
          </div>

          {/* The project and its phase as one control: the phase list has to be
              re-read and the phase id dropped whenever the project changes, and a
              second copy of that rule is a dialog one edit away from forgetting
              it. See `_shared/ProjectPhaseFields`. */}
          <ProjectPhaseFields
            projects={projects}
            projectId={projectId}
            milestoneId={milestoneId}
            onProjectChange={setProjectId}
            onMilestoneChange={setMilestoneId}
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="crm-lab">{t("tasks.priority")}</span>
              <MenuField
                variant="form"
                value={priority}
                onChange={setPriority}
                label={t("tasks.priority")}
                options={PRIORITIES.map((p) => ({ value: p, label: t(`tasks.prio.${p}`) }))}
              />
            </div>
            <div>
              <span className="crm-lab">{t("tasks.estimateHours")}</span>
              <label className="crm-inp">
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  inputMode="decimal"
                  value={estimate}
                  onChange={(e) => setEstimate(e.target.value)}
                />
              </label>
            </div>
          </div>

          {/* Both dates on one line: the pair is read together, and a start date
              on its own row reads as a second, unrelated question. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="crm-lab">{t("tasks.startDate")}</span>
              <DatePicker value={start} onChange={setStart} locale={locale} placeholder={t("tasks.startDate")} />
            </div>
            <div>
              <span className="crm-lab">{t("tasks.dueDate")}</span>
              <DatePicker value={due} onChange={setDue} locale={locale} placeholder={t("tasks.dueDate")} />
            </div>
          </div>

          {/* Always drawn, and empty is a legible answer rather than a fault: on
              the day this shipped every task in the register had no owner, so a
              field that hid itself when nobody was picked would be a field nobody
              ever saw. The line under it says what the empty box means, because a
              search box with no chips beneath it reads as a control that failed to
              load rather than as a task nobody is on. */}
          <div>
            <span className="crm-lab">{t("projects.assignees")}</span>
            <UserPicker multiple value={assignees} onChange={setAssignees} />
            {assignees.length === 0 ? (
              <p className="crm-mut mt-1.5 text-[12.5px]">{t("projects.unassigned")}</p>
            ) : null}
          </div>

          <CheckField checked={billable} onChange={setBillable} label={t("tasks.billable")} />

          {/* Last, and under a heading of its own - the same shape the facts card
              on the task page uses, so the question looks the same wherever it is
              answered. It is not a second billing box: everything above this line
              is between us and our invoice, and this one is about what the
              customer opens. */}
          <div>
            <span className="crm-lab">{t("projects.clientVisibility")}</span>
            <CheckField
              checked={visibleToClient}
              onChange={setVisibleToClient}
              label={t("projects.visibleToClient")}
            />
          </div>

          {/* Inside the dialog rather than in a toast: the toast host draws a tick
              beside its message and lives at the bottom of the page, which is the
              wrong voice and the wrong place for the reason a form did not submit.
              The strip sits where the eye already is - just above the button that
              was pressed. */}
          {errorKey ? <p className="crm-alert text-[13px]">{t(errorKey)}</p> : null}

          <div className="crm-m-foot">
            <Btn type="button" onClick={onClose} disabled={busy}>
              {t("common.cancel")}
            </Btn>
            <Btn type="submit" tone="brand" disabled={busy || !title.trim()}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
              {t("common.add")}
            </Btn>
          </div>
        </form>
      </PopHost>
    </Modal>
  );
}
